import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  InputText,
  Select,
  Typography,
  SectionCard,
  type SelectOption,
} from "@/deps/ui-library";
import { api } from "@/api";
import {
  useAccounts,
  usePartyDetails,
  useSaveBillDraft,
  useSubmitBill,
  useTaxCodes,
} from "@/api/queries";
import type { Bill, NewBill, Party } from "@/api/types";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { calculateTotals } from "@/lib/invoiceTotals";
import { today } from "@/lib/dates";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { formatAmount } from "@/lib/money";
import { PAYMENT_TERMS, dueFrom } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";

/**
 * Record a supplier bill.
 *
 * The screen splits recoverable from blocked input tax as the lines are entered, because the two
 * post differently and the difference is invisible afterwards: recoverable VAT offsets what you owe
 * on sales, blocked VAT is capitalised into the expense and reaches the profit and loss
 * (`05-nepal-compliance.md` RULE 3.4).
 *
 * Given a draft and its supplier, it edits that draft.
 */
export function NewBillPage({ editing }: { editing?: { bill: Bill; supplier: Party } } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();

  // The chart and the tax table come from the API. This screen used to carry four expense accounts
  // of its own — `acc_5050`, `acc_5060`, `acc_5150`, `acc_1010` — none of which were in any chart
  // of accounts, and the last of which was labelled "Property, plant and equipment" while `ac_1010`
  // in the real chart is the Nabil bank account. The tax codes it sent, `tax_vat13` and
  // `tax_vat13_blocked`, did not exist either.
  const { data: accounts } = useAccounts();
  const { data: taxCodes } = useTaxCodes();
  const saveDraft = useSaveBillDraft();
  const submit = useSubmitBill();

  // Expenses first, then the capital accounts a bill may also post to. A purchase is an expense
  // until it is not, and the default should be the common case.
  const expenseAccounts = [
    ...(accounts ?? []).filter((a) => a.isActive && a.group === "Expenses"),
    ...(accounts ?? []).filter((a) => a.isActive && a.group === "Assets"),
  ];
  const vat = taxCodes?.find((code) => code.recoverable && code.ratePct === "13");
  const blocked = taxCodes?.find((code) => !code.recoverable && code.ratePct === "13");

  const draft = editing?.bill;
  const [draftId, setDraftId] = useState<string | undefined>(draft?.id);
  const [supplier, setSupplier] = useState<Party | null>(editing?.supplier ?? null);
  usePartyFromAddress("supplier", setSupplier);
  const [date, setDate] = useState(draft?.date ?? today());
  // Terms set the due date (ADR-0043): the bill's, or else the supplier's, and a date typed here stands.
  // With none it is the bill date. A draft keeps the date and the terms it was saved with.
  const [termsOnBill, setTermsOnBill] = useState<string | null>(
    draft ? (draft.paymentTermsDays ?? "") : null,
  );
  const [typedDue, setTypedDue] = useState<string | null>(draft?.due ?? null);
  const { data: supplierDetails } = usePartyDetails(supplier?.id ?? "", supplier !== null);
  const terms = termsOnBill ?? supplierDetails?.paymentTermsDays ?? "";
  const due = typedDue ?? (dueFrom(date, terms) || date);
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState(draft?.vendorInvoiceNo ?? "");
  const [recoverable, setRecoverable] = useState(
    draft ? Number(draft.nonRecoverableTax) === 0 : true,
  );
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.description,
          accountId: line.accountId,
          category: "S",
          rate: "13",
          grossPrice: line.unitPrice,
          quantity: line.quantity,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );

  /**
   * The lines as the screen shows and sends them.
   *
   * The chart arrives after the first render, so a new line starts with no account while the select
   * beneath it renders the first option — a line that looks chosen and is not. Filling the gap here
   * rather than in an effect means what is rendered and what is sent are the same value, computed
   * once, with no second render to go wrong.
   */
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: expenseAccounts[0]?.id ?? "" } : line,
  );

  const totals = calculateTotals(withAccounts);
  const taxCodeId = (recoverable ? vat?.id : blocked?.id) ?? "";
  const ready =
    supplier !== null && taxCodeId !== "" && withAccounts.every((l) => l.accountId !== "");
  const canSubmit = ready && vendorInvoiceNo.trim() !== "" && Number(totals.totalWithVat) > 0;
  const pending = saveDraft.isPending || submit.isPending;
  const refusal = saveDraft.error ?? submit.error;

  const dto = (): NewBill => ({
    id: draftId,
    supplierId: supplier!.id,
    date,
    due,
    paymentTermsDays: terms === "" ? undefined : terms,
    vendorInvoiceNo,
    lines: withAccounts.map((line) => ({
      description: line.description,
      accountId: line.accountId,
      quantity: line.quantity,
      unitPrice: line.grossPrice,
      taxCodeId,
      capital: accounts?.find((account) => account.id === line.accountId)?.group === "Assets",
    })),
    attachmentIds: [],
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={draft ? "Edit bill" : "New bill"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() =>
                void navigate(
                  draft
                    ? { to: "/purchases/bills/$id", params: { id: draft.id } }
                    : { to: "/purchases/bills" },
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              disabled={!ready || pending}
              onClick={() => {
                // This used to navigate away without saving anything. The bill was gone, and the
                // screen gave every sign of having kept it.
                saveDraft.mutate(dto(), {
                  onSuccess: (bill) => {
                    setDraftId(bill.id);
                    void navigate({ to: "/purchases/bills" });
                  },
                });
              }}
            >
              {saveDraft.isPending ? "Saving" : "Save draft"}
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSubmit || pending}
              onClick={() => {
                // The refusal used to be swallowed by `.catch(() => undefined)`: the person pressed
                // Submit, nothing happened, and nothing said why.
                submit.mutate(dto(), {
                  onSuccess: () => void navigate({ to: "/purchases/bills" }),
                });
              }}
            >
              {submit.isPending ? "Submitting" : "Submit for approval"}
            </Button>
          </span>
        }
      />

      {refusal ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={refusal instanceof Error ? refusal.message : "That bill could not be saved."}
        />
      ) : null}

      <div className="flex flex-col gap-4">
        <SectionCard title="Bill">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <PartyCombobox
              label="Supplier"
              required
              value={supplier}
              onChange={(party) => {
                setSupplier(party);
                setTermsOnBill(null);
                setTypedDue(null);
              }}
              search={(q) => api.parties.search("supplier", q)}
            />

            <InputText
              label="Their invoice number"
              required
              value={vendorInvoiceNo}
              onChange={(e) => setVendorInvoiceNo(e.target.value)}
              className="num"
            />

            <InputText
              label="Bill date"
              required
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />

            <Select
              label="Payment terms"
              options={PAYMENT_TERMS}
              value={terms}
              onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
                const option = Array.isArray(details.value) ? details.value[0] : details.value;
                setTermsOnBill(option ? String(option.value) : "");
                setTypedDue(null);
              }}
              showClearButton={false}
            />

            <InputText
              label="Due"
              required
              type="date"
              value={due}
              onChange={(e) => setTypedDue(e.target.value)}
            />
          </div>
        </SectionCard>

        <div className="flex flex-col gap-4">
          <SectionCard title="Lines">
            <LineEditor lines={withAccounts} onChange={setLines} accounts={expenseAccounts} />
          </SectionCard>

          <SectionCard title="Tax">
            <Checkbox
              label="Input tax on this bill is recoverable"
              checked={recoverable}
              onCheckedChange={(details) => setRecoverable(details.checked === true)}
            />

            <dl className="mt-3 flex flex-col gap-2 text-sm">
              <div className="flex items-baseline justify-between">
                <dt className="text-fg-muted">Subtotal</dt>
                <dd className="num text-fg">{formatAmount(totals.totalWithoutVat)}</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-fg-muted">
                  {recoverable ? say("Recoverable VAT") : say("Blocked VAT")}
                </dt>
                <dd className="num text-fg">{formatAmount(totals.totalVatAmount)}</dd>
              </div>
              <div className="border-line-soft flex items-baseline justify-between border-t pt-2 font-semibold">
                <dt className="text-fg">Total</dt>
                <dd className="num text-fg">{formatAmount(totals.totalWithVat)}</dd>
              </div>
            </dl>

            <p className="text-fg-muted mt-3 text-xs">
              {recoverable
                ? say(
                    "Recoverable tax is posted separately from the expense and offsets the VAT you owe on sales.",
                  )
                : "Blocked tax is capitalised into the expense rather than claimed back, so the whole amount reaches the profit and loss."}
            </p>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
